import { useEffect } from 'react';
import { usePerItemSaveUiState, useConfirmingDelete } from '../shared/hooks';
import { deleteItemFlow } from '../shared/deleteFlow';

export type BuilderMode = 'create' | 'edit';

export function usePropertyEntityTabController<T extends { id?: string; propertyID?: string }>(args: {
  mode: BuilderMode;
  resolvedPropertyID: string;

  // source of truth
  localItems: T[];
  storeItems: T[];
  writeLocal: (next: T[]) => void;
  writeStore: (next: T[]) => void;

  // fetching
  hasFetched: boolean;
  fetchRemote: () => Promise<unknown> | void;

  // dirty tracking
  markDirty: (id: string) => void;
  markClean: (id: string) => void;

  // CRUD thunks (already wrapped by caller)
  createRemote: (item: T, tempId: string) => Promise<unknown>;
  updateRemote: (item: T) => Promise<unknown>;
  deleteRemote: (id: string) => Promise<unknown>;

  // misc
  entityName: string; // "location" | "system"
  isTempId: (id: string) => boolean;
  normalizeBeforeSave: (item: T) => T;
}) {
  const {
    mode,
    resolvedPropertyID,
    localItems,
    storeItems,
    writeLocal,
    writeStore,
    hasFetched,
    fetchRemote,
    markClean,
    createRemote,
    updateRemote,
    deleteRemote,
    entityName,
    isTempId,
    normalizeBeforeSave,
  } = args;

  const { savingById, saveErrorById, setSaving, setSaveError, clearItem } = usePerItemSaveUiState();
  const { confirmDelete } = useConfirmingDelete();

  const items = mode === 'edit' ? storeItems : localItems;

  const writeAll = (next: T[]) => {
    if (mode === 'edit') {
      if (!resolvedPropertyID) return;
      writeStore(next);
      return;
    }
    writeLocal(next);
  };

  // fetch-once in edit mode
  useEffect(() => {
    if (mode !== 'edit') return;
    if (!resolvedPropertyID) return;
    if (hasFetched) return;
    void fetchRemote();
  }, [mode, resolvedPropertyID, hasFetched, fetchRemote]);

  const saveItem = async (item: T) => {
    if (mode !== 'edit') return;
    if (!resolvedPropertyID) return;
    if (!item.id) return;

    const id = item.id;
    setSaving(id, true);
    setSaveError(id, null);

    try {
      const normalized = normalizeBeforeSave(item);

      if (isTempId(id)) {
        await createRemote(normalized, id);
      } else {
        await updateRemote(normalized);
      }

      markClean(id);
      setSaveError(id, null);
    } catch (e) {
      const msg =
        typeof e === 'object' && e != null && 'message' in e && typeof (e as { message: string }).message === 'string'
          ? (e as { message: string }).message
          : `Failed to save ${entityName}`;
      setSaveError(id, msg);
    } finally {
      setSaving(id, false);
    }
  };

  const deleteItem = async (item: T) => {
    if (!resolvedPropertyID) return;

    await deleteItemFlow({
      item,
      entityName,
      isTempId,
      confirm: confirmDelete,

      onRemoveLocal: () => {
        if (!item.id) return;
        writeAll(items.filter((x) => x.id !== item.id));
      },

      onDeleteRemote: async () => {
        if (!item.id) return;

        const id = item.id;
        setSaving(id, true);
        setSaveError(id, null);

        try {
          await deleteRemote(id);
          clearItem(id);
        } catch (e) {
          const msg =
            typeof e === 'object' &&
            e != null &&
            'message' in e &&
            typeof (e as { message: string }).message === 'string'
              ? (e as { message: string }).message
              : `Failed to delete ${entityName}`;
          setSaveError(id, msg);
        } finally {
          setSaving(id, false);
        }
      },
    });
  };

  return {
    items,
    writeAll,

    savingById,
    saveErrorById,

    saveItem,
    deleteItem,
  };
}
